// Initialize i18n before other imports
import '@/i18n';

import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider, ProtectedRoute, useAuth } from '@/auth';
import { setSessionToken } from '@/auth/sessionToken';
import { apiService } from '@/api';
import { trackLogin } from '@/services/analyticsService';
import { captureEmailAttribution } from '@/services/emailAttribution';
import { Layout } from '@/components/layout';
import { LoginPage, OAuthPage, OAuthCallbackPage } from '@/features/auth';
import { HomePage } from '@/features/home';
import { FilesPage } from '@/features/files';
import { SubscriptionPage } from '@/features/subscription';
import { AccountPage } from '@/features/account';
import { ExamplesPage } from '@/features/examples';
import { RewardsPage } from '@/features/rewards';
import { AmplitudeRouteTracker } from '@/components/analytics/AmplitudeRouteTracker';
import { useSettingsStore } from '@/store/settingsStore';
import { useUserStore } from '@/store/userStore';
import { useBanStore } from '@/store/banStore';
import { BannedPage } from '@/components/BannedPage';
import { resolveUiVersion, showsV2PricingPage } from '@/v2/canUseV2';
import { V2App } from '@/v2/V2App';
import { SharePage } from '@/v2/pages/SharePage';
import { UpgradePage } from '@/v2/pages/UpgradePage';
import { SocialCallbackPage } from '@/v2/pages/SocialCallbackPage';
import { CheckoutRedirectPage } from '@/v2/pages/CheckoutRedirectPage';
import { useUiVariant } from '@/v3/uiVariant';
import { V3Fork, SwitchBarSlot } from '@/v3/fork';

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/* Boundary above everything: a render throw anywhere below shows a retry
          screen instead of unmounting to a black page (the top "blank screen" bug). */}
      <ErrorBoundary>
        <AppShell />
      </ErrorBoundary>
    </BrowserRouter>
  );
}

// The public /share/:id page renders OUTSIDE the auth provider and app shell, so a shared
// link opens instantly for anyone — signed in or not, V1 or V2 — without the auth "Loading…"
// gate. Every other path is the normal authed app.
function AppShell() {
  const { pathname } = useLocation();
  const isBanned = useBanStore((s) => s.isBanned);
  const banReason = useBanStore((s) => s.reason);
  // A suspended account overrides the entire authed app: once the backend
  // rejects the session with 403 user_banned (login or any gated call), the
  // ban interceptor flips this flag and we render the suspended screen instead.
  // Placed after the public /share and /connect legs so those unauthenticated
  // pages still open for direct links.
  if (pathname.startsWith('/share/')) {
    return (
      <Routes>
        <Route path="/share/:id" element={<SharePage />} />
      </Routes>
    );
  }
  // Social OAuth return leg (TikTok etc.) — same shell-level treatment as /share/:
  // works for every cohort without touching the V1/V2 fork, and identifies the
  // user from localStorage so it skips the auth "Loading…" gate.
  if (pathname.startsWith('/connect/')) {
    return (
      <Routes>
        <Route path="/connect/:provider/callback" element={<SocialCallbackPage />} />
      </Routes>
    );
  }
  // Mobile-app checkout return (signed in or not): iOS hands it to vivideo://, others go to /upgrade.
  if (pathname.startsWith('/checkout_redirect')) {
    return <CheckoutRedirectPage />;
  }
  if (isBanned) {
    return <BannedPage reason={banReason} />;
  }
  return (
      <AuthProvider>
      <AmplitudeRouteTracker />
      <RootRoutes />
    </AuthProvider>
  );
}

// Branch between the live V1 app and the V2 redesign. V2 is now the default for
// everyone (see resolveUiVersion), so the AUTH GATE must live here — V2App has no
// ProtectedRoute and no /login route of its own, so without this an unauthenticated
// visitor would land in the V2 app as a guest instead of being sent to login.
function RootRoutes() {
  const uiVersion = useSettingsStore((s) => s.uiVersion);
  const abGroup = useUserStore((s) => s.user?.abGroup);
  const { isAuthenticated: isFirebaseAuthenticated, isLoading } = useAuth();
  const userId = useUserStore((s) => s.userId);
  const user = useUserStore((s) => s.user);
  const location = useLocation();
  const { pathname } = location;
  // Same "logged in" signal V1's ProtectedRoute uses (Firebase OR bypass-auth uid).
  const isAuthenticated = isFirebaseAuthenticated || (!!userId && !!user);
  const isPublicAuthPath = pathname === '/login' || pathname.startsWith('/oauth');
  // V3 switch (src/v3/uiVariant.ts): 'v2' unless V3 is switched on for this user.
  const ui = useUiVariant(isAuthenticated ? user?.id || userId : null);

  // Recovery-email one-tap login: a ?lt= ticket signs the user in (no login wall)
  // before the auth gate below. Handled only when NOT already signed in — an
  // existing session is kept and the ticket ignored.
  const loginTicket = new URLSearchParams(location.search).get('lt');
  if (loginTicket && !isAuthenticated && !isLoading) {
    return <EmailLinkLogin ticket={loginTicket} />;
  }

  // While auth resolves, hold rendering (no guest flash, and abGroup is unknown so
  // the V2/V1 fork below isn't decidable yet). Public auth pages render immediately.
  if (isLoading && !isPublicAuthPath) return null;

  // Login / OAuth pages live in V1Routes — always render them there.
  if (isPublicAuthPath) return <V1Routes />;

  // AUTH GATE: not signed in → the login page (V1Routes' LoginPage), remembering
  // where they were headed (path + query) so post-login they land back there
  // (e.g. an email CTA whose one-tap ticket had expired). Prevents the "opens as
  // a fake guest with no login" bug when V2 is the default for all.
  if (!isAuthenticated) {
    // Keep a recovery-email link's identity even if the login flow drops the query.
    captureEmailAttribution(location.search);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Already signed in with a ticket (any route, e.g. the create page): keep the
  // email identity for the session and drop the unused one-time ticket from the URL.
  if (loginTicket) {
    captureEmailAttribution(location.search);
    const params = new URLSearchParams(location.search);
    params.delete('lt');
    const search = params.toString() ? `?${params.toString()}` : '';
    return <Navigate to={{ pathname, search, hash: location.hash }} replace />;
  }

  // V3 fork: before the V2 branch, after the ?lt= step and the auth gate. Inert while V3 is off.
  if (ui === 'pending') return null;
  if (ui === 'v3') return <V3Fork fallback={<V2App />} />;

  if (resolveUiVersion(uiVersion, abGroup) === 'v2') {
    return <><SwitchBarSlot /><V2App /></>;
  }
  return <V1Routes />;
}

// Consumes a recovery-email login ticket (?lt=): exchanges it for a session
// token, establishes the same store session the OTP/impersonation flows use, then
// continues to the same route (paywall or create page) with the ticket stripped. On failure it
// falls back to the normal login page, preserving the intended destination.
function EmailLinkLogin({ ticket }: { ticket: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setUserId } = useUserStore();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    // Session copy of the email identity, in case a redirect below drops the query.
    captureEmailAttribution(location.search);

    // Destination = current URL minus the one-time ticket.
    const params = new URLSearchParams(location.search);
    params.delete('lt');
    const dest = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;

    void (async () => {
      try {
        const result = await apiService.emailLoginExchange(ticket);
        if (result?.sessionToken) {
          setSessionToken(result.sessionToken);
          const signedInUser = await apiService.getUserInfo(result.userId);
          setUserId(result.userId);
          setUser(signedInUser);
          localStorage.setItem('vivideo_user_id', result.userId);
          localStorage.setItem('vivideo_email_auth', result.userId);
          trackLogin('bypass');
          navigate(dest, { replace: true });
          return;
        }
      } catch (err) {
        console.error('Email login exchange failed:', err);
      }
      // Expired/invalid ticket → normal login, returning to the same destination.
      navigate('/login', { replace: true, state: { from: { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' } } });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

// PRICING-PAGE TEST: ab groups 12-13 get the V2 pricing page inside the
// otherwise-untouched V1 app, so the two designs can be compared on the same
// funnel events. Exception: Stripe TRIAL activation returns (?trial=true) are
// handled only by the V1 page — route those there regardless, so an in-flight
// trial is never dropped.
function PricingRoute() {
  const abGroup = useUserStore((s) => s.user?.abGroup);
  const { search } = useLocation();
  const isTrialReturn = new URLSearchParams(search).get('trial') === 'true';
  if (showsV2PricingPage(abGroup) && !isTrialReturn) return <UpgradePage />;
  return <SubscriptionPage />;
}

// The live v1 routes — unchanged from before the switch was added.
function V1Routes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/oauth" element={<OAuthPage />} />
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<HomePage />} />
        <Route path="/files" element={<FilesPage />} />
        <Route path="/subscription" element={<PricingRoute />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/examples" element={<ExamplesPage />} />
        <Route path="/rewards" element={<RewardsPage />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
